import type {
  GuidePageConfig,
  ProductAccent,
  ProductLogo,
  ProductMedia,
  ProductSlug,
  ProductStage,
  ScreenConfig,
} from '../models/product.model';
import { AGENT_ROUTE, ONBOARDING } from './site.config';

/**
 * The product register.
 *
 * Identity, routing and calls-to-action — what the site chrome needs to list
 * the products: the header, the footer, the home page and the 404. Each
 * product's pages are its own, under `pages/<slug>/`, and every word about it
 * lives under `<slug>` in the translation files.
 */
export interface ProductConfig {
  readonly slug: ProductSlug;
  readonly accent: ProductAccent;
  readonly stage: ProductStage;
  /** The area's address, '/' plus the slug. */
  readonly route: string;
  /** The pages its area has, as the sub-nav, the footer and the trust strip link to them. */
  readonly sections: readonly ProductSectionKey[];
  readonly media: ProductMedia;
  readonly contactHref: string;
  readonly overviewScreen: ScreenConfig;
  readonly pricingKey: string;
  readonly primaryCta: { readonly href: string; readonly external?: boolean };
  readonly secondaryCta?: { readonly href: string; readonly external?: boolean };
  /**
   * Length of the free trial, in days. Kept in step with the same figure in
   * `pricing.config.ts` — one is the badge on the product, the other is the
   * terms on the price. Omitted where there is no trial.
   */
  readonly trialDays?: number;
  /** The lockup in `public/brand/` that identifies this product. */
  readonly logo?: ProductLogo;
  /** Task pages of the product's guide; titles and bodies live under `<slug>.guide`. */
  readonly guidePages: readonly GuidePageConfig[];
}

/** The sections of a product area, in sub-nav order; `key` is `site.nav.product.<key>`. */
export const PRODUCT_SECTIONS = [
  { key: 'overview', path: '' },
  { key: 'pricing', path: 'pricing' },
  { key: 'guide', path: 'guide' },
  { key: 'faq', path: 'faq' },
  { key: 'whatsNew', path: 'whats-new' },
  { key: 'security', path: 'security' },
] as const;

export type ProductSectionKey = (typeof PRODUCT_SECTIONS)[number]['key'];

/** The sections a product's footer column links to; labels reuse `site.nav.product.<key>`. */
export const FOOTER_PRODUCT_SECTIONS: ReadonlySet<string> = new Set([
  'pricing',
  'whatsNew',
  'security',
]);

/** Anchors on a security page, nested under it in the footer; label is `site.footer.product.<key>`. */
export const FOOTER_SECURITY_ANCHORS = [
  { key: 'access', fragment: 'consent' },
  { key: 'payment', fragment: 'payments' },
  { key: 'limits', fragment: 'limits' },
  { key: 'legal', fragment: 'legal' },
] as const;

/** Home is Catalog-led, so a product-scoped link shown outside a product area resolves here. */
export const DEFAULT_PRODUCT_SLUG: ProductSlug = 'catalog';

export const PRODUCT_CONFIG: readonly ProductConfig[] = [
  {
    slug: 'catalog',
    accent: 'catalog',
    stage: 'live',
    route: '/catalog',
    sections: ['overview', 'pricing', 'guide', 'faq', 'whatsNew', 'security'],
    media: 'catalog',
    contactHref: '/contact?product=catalog',
    overviewScreen: { name: 'workspace' },
    pricingKey: 'catalog',
    primaryCta: { href: ONBOARDING.catalog, external: true },
    secondaryCta: { href: '/catalog/pricing' },
    trialDays: 30,
    logo: 'catalog',
    guidePages: [
      {
        slug: 'get-started',
        order: 1,
        screens: [
          { name: 'onboarding-organization', after: 0 },
          { name: 'onboarding-plan', after: 1 },
          { name: 'onboarding-permissions', after: 2 },
          { name: 'workspace', after: 4 },
        ],
      },
      {
        slug: 'publish-to-the-catalog',
        order: 2,
        screens: [
          { name: 'application-management', after: 0 },
          { name: 'group-management', after: 1 },
        ],
      },
      {
        slug: 'build-an-approval-chain',
        order: 3,
        screens: [{ name: 'group-detail', after: 1 }],
      },
      {
        slug: 'browse-and-request',
        order: 4,
        screens: [
          { name: 'applications', after: 0 },
          { name: 'groups', after: 0 },
          { name: 'application-approval', after: 1 },
          { name: 'application-detail', after: 2 },
        ],
      },
      {
        slug: 'track-your-requests',
        order: 5,
        screens: [
          { name: 'my-demands', after: 0 },
          { name: 'demand-started', after: 1 },
          { name: 'demand-step-approved', after: 1 },
        ],
      },
      {
        slug: 'approve-a-request',
        order: 6,
        screens: [
          { name: 'my-approvals', after: 0 },
          { name: 'approval-review', after: 1 },
        ],
      },
      {
        slug: 'delegate-approvals',
        order: 7,
        screens: [
          { name: 'delegation', after: 0 },
          { name: 'delegation-form', after: 1 },
        ],
      },
      {
        slug: 'reports-and-export',
        order: 8,
        screens: [
          { name: 'all-demands', after: 0 },
          { name: 'all-demands-filters', after: 1 },
        ],
      },
      { slug: 'capabilities', order: 9 },
      { slug: 'use-cases', order: 10 },
    ],
  },
  {
    slug: 'core',
    accent: 'core',
    stage: 'pilot',
    route: '/core',
    // No guide or security page while Core is in pilot. The Agent's link comes from `AGENT_ROUTE`.
    sections: ['overview', 'pricing', 'faq', 'whatsNew'],
    media: 'core',
    contactHref: '/contact?product=core',
    overviewScreen: { name: 'device-security' },
    pricingKey: 'core',
    primaryCta: { href: '/contact?product=core' },
    secondaryCta: { href: AGENT_ROUTE },
    logo: 'core',
    // Kept for when the guide comes back: no page reads it while Core is in pilot.
    guidePages: [
      {
        slug: 'how-it-works',
        order: 1,
        screens: [
          { name: 'licences', after: 1, ratio: '5 / 2' },
          { name: 'device-detail', after: 2, ratio: '5 / 2' },
          { name: 'permission-scope', after: 3, ratio: '5 / 2' },
        ],
      },
    ],
  },
];

/** The product whose area holds `path`: its own pages, and the Agent's under Core. */
export function productByPath(path: string): ProductConfig | undefined {
  return PRODUCT_CONFIG.find(
    (product) => path === product.route || path.startsWith(`${product.route}/`),
  );
}
